import { Component } from '@theme/component';
import { bindFilterSort } from '@theme/bt-filter-sort';
import { filtersToSearchParams, hasShopifyFilters } from '@theme/bt-shopify-filter-params';
import { sectionRenderer } from '@theme/section-renderer';
import {
  appendMerchAfter,
  appendPlacedMerch,
  placeMerchTiles,
  readMerchConfig,
  syncShopTheLookGutter,
} from '@theme/bt-plp-merch';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [loading]
 * @property {HTMLElement} grid
 * @property {HTMLElement} sentinel
 * @property {HTMLElement} showing
 * @property {HTMLButtonElement} loadMore
 * @property {HTMLElement} more
 * @property {HTMLElement} empty
 */

/**
 * Bo+Tee collection grid — Shopify merchandising order and storefront filters.
 * @extends {Component<Refs>}
 */
class BtCollectionShopify extends Component {
  requiredRefs = ['grid', 'sentinel', 'showing', 'loadMore', 'more', 'empty'];

  /** @type {ReturnType<typeof bindFilterSort> | null} */
  #filterSort = null;

  /** @type {import('@theme/bt-plp-merch').MerchTile[]} */
  #merch = [];

  /** @type {MediaQueryList | null} */
  #layoutMql = null;

  /** @type {number} */
  #page = 1;

  /** @type {number} */
  #pages = 1;

  /** @type {number} */
  #total = 0;

  /** @type {number} */
  #productCount = 0;

  /** @type {boolean} */
  #loading = false;

  /** @type {boolean} */
  #hasActiveFilters = false;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #resizeTimer = null;

  /** @type {AbortController | null} */
  #facetAbort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#page = Number(this.dataset.page) || 1;
    this.#pages = Number(this.dataset.pages) || 1;
    this.#total = Number(this.dataset.total) || 0;
    this.#merch = readMerchConfig(this);
    this.#layoutMql = window.matchMedia('(min-width: 990px)');
    this.#layoutMql.addEventListener('change', this.#onLayoutChange);
    this.#filterSort = bindFilterSort({
      root: this.#filterSortRoot(),
      colourMode: this.dataset.colourMode === 'specific' ? 'specific' : 'family',
      onApply: ({ filter, sort, sortKey }) => this.#onFilterApply(filter, sort, sortKey),
      onDraftChange: ({ filter, sort, sortKey }) => this.#previewFacets(filter, sort, sortKey),
    });
    this.#hydrateFilters();
    this.#countProducts();
    this.#syncMerch();
    this.#syncPagination();
    this.#observeSentinel();
    this.addEventListener('click', this.#onShopTheLookClick);
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#facetAbort?.abort();
    this.#facetAbort = null;
    this.#filterSort?.disconnect();
    this.#filterSort = null;
    this.#layoutMql?.removeEventListener('change', this.#onLayoutChange);
    this.#layoutMql = null;
    this.removeEventListener('click', this.#onShopTheLookClick);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('popstate', this.#onPopState);
    if (this.#resizeTimer) clearTimeout(this.#resizeTimer);
  }

  /**
   * @param {Event} event
   */
  handleDescriptionMore(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const root = button.closest('[data-bt-plp-description]');
    if (!(root instanceof HTMLElement)) return;
    const excerpt = root.querySelector('[data-bt-plp-description-excerpt]');
    const full = root.querySelector('[data-bt-plp-description-full]');
    if (excerpt instanceof HTMLElement) excerpt.hidden = true;
    if (full instanceof HTMLElement) full.hidden = false;
    root.classList.add('is-expanded');
    button.hidden = true;
  }

  /**
   * @param {Event} [event]
   */
  handleLoadMore(event) {
    event?.preventDefault?.();
    if (this.#loading || !this.#hasMore()) return;
    this.#loadPage(this.#page + 1, { append: true });
  }

  #filterSortRoot() {
    return (
      this.querySelector('bt-filter-sort') ||
      document.querySelector('bt-collection-header bt-filter-sort')
    );
  }

  #isDesktop() {
    return Boolean(this.#layoutMql?.matches);
  }

  #usesInfinite() {
    const strategy = (this.dataset.paginationStrategy || 'button').trim().toLowerCase();
    return strategy === 'infinite' || strategy === 'infinite load';
  }

  #hasMore() {
    return this.#page < this.#pages;
  }

  #sectionId() {
    return (this.dataset.sectionId || this.closest('.shopify-section')?.id || '')
      .replace(/^shopify-section-/, '')
      .trim();
  }

  #countProducts() {
    this.#productCount = this.refs.grid.querySelectorAll(':scope > [data-bt-plp-product]').length;
  }

  #readFilterPayload(root = this) {
    const script = root.querySelector('[data-bt-plp-shopify-filters]');
    if (!(script instanceof HTMLScriptElement)) return null;
    try {
      return JSON.parse(script.textContent || '');
    } catch {
      return null;
    }
  }

  #hydrateFilters(root = this) {
    const payload = this.#readFilterPayload(root);
    if (!payload || !this.#filterSort) return;

    const facets = Array.isArray(payload.filters) ? payload.filters : [];
    const sortKey = payload.sort_by || payload.default_sort || 'manual';
    const sortOptions = Array.isArray(payload.sort_options) ? payload.sort_options : [];
    if (typeof this.#filterSort.hydrateFromFacets === 'function') {
      this.#filterSort.hydrateFromFacets(facets, sortKey, sortOptions);
    } else {
      this.#filterSort.setCatalogFacets(facets);
      this.#filterSort.setFacets(facets);
    }
    this.#filterSort.setTotal(this.#total);
    this.#hasActiveFilters = hasShopifyFilters(payload);
  }

  /**
   * @param {import('@theme/bt-nosto-search').NostoSearchFilter[]} filter
   * @param {import('@theme/bt-nosto-search').NostoSearchSort[]} sort
   * @param {string} [sortKey]
   */
  async #onFilterApply(filter, sort, sortKey) {
    this.#facetAbort?.abort();
    const next = new URL(window.location.href);
    next.search = filtersToSearchParams(filter, sort, window.location.href, sortKey).toString();
    history.pushState({ btPlp: true }, '', next);
    await this.#loadPage(1, { url: next, replace: true });
  }

  /**
   * Refresh other facet counts while the panel is open, without applying the grid.
   * @param {import('@theme/bt-nosto-search').NostoSearchFilter[]} filter
   * @param {import('@theme/bt-nosto-search').NostoSearchSort[]} sort
   * @param {string} [sortKey]
   */
  async #previewFacets(filter, sort, sortKey) {
    const sectionId = this.#sectionId();
    if (!sectionId) return;

    const draft = Array.isArray(filter) ? filter : [];
    this.#facetAbort?.abort();
    const abort = new AbortController();
    this.#facetAbort = abort;

    const url = new URL(window.location.href);
    url.search = filtersToSearchParams(draft, sort, window.location.href, sortKey).toString();
    url.searchParams.delete('page');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url, abort.signal);
      if (abort.signal.aborted) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('bt-collection-shopify') || doc.body;
      const payload = this.#readFilterPayload(next);
      const facets = Array.isArray(payload?.filters) ? payload.filters : [];
      if (facets.length) this.#filterSort?.setFacets(facets);
      else if (draft.length) this.#filterSort?.setFacets([]);

      const total = Number(next instanceof HTMLElement ? next.getAttribute('data-total') : NaN);
      if (Number.isFinite(total)) this.#filterSort?.setTotal(total);
    } catch (error) {
      if (abort.signal.aborted) return;
      console.warn('[bt-collection-shopify] Filter preview failed', error);
    }
  }

  #onPopState = () => {
    this.#loadPage(1, { url: new URL(window.location.href), replace: true });
  };

  /**
   * @param {number} page
   * @param {{ url?: URL, replace?: boolean, append?: boolean }} [options]
   */
  async #loadPage(page, options = {}) {
    const sectionId = this.#sectionId();
    if (!sectionId || this.#loading) return;

    this.#loading = true;
    if (options.replace) this.#showLoading();
    else if (options.append) this.#showMoreLoading();

    const url = new URL(options.url || window.location.href);
    if (options.append) url.searchParams.set('page', String(page));
    else url.searchParams.delete('page');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('bt-collection-shopify') || doc.body;
      if (!next) return;

      const nextGrid = next.querySelector('[ref="grid"]');
      const nextEmpty = next.querySelector('[ref="empty"]');
      this.#page = Number(next.getAttribute('data-page')) || page;
      this.#pages = Number(next.getAttribute('data-pages')) || 1;
      this.#total = Number(next.getAttribute('data-total')) || 0;
      this.dataset.page = String(this.#page);
      this.dataset.pages = String(this.#pages);
      this.dataset.total = String(this.#total);

      const nextFilters = next.querySelector('[data-bt-plp-shopify-filters]');
      const currentFilters = this.querySelector('[data-bt-plp-shopify-filters]');
      if (nextFilters && currentFilters) currentFilters.replaceWith(nextFilters.cloneNode(true));
      if (options.replace || !options.append) this.#hydrateFilters();

      if (options.replace || !options.append) {
        this.refs.grid.replaceChildren();
        this.#productCount = 0;
        if (nextGrid) {
          for (const item of [...nextGrid.querySelectorAll(':scope > [data-bt-plp-product]')]) {
            this.refs.grid.append(item);
          }
        }
        this.#countProducts();
        const empty = this.#productCount === 0;
        this.refs.empty.hidden = !empty;
        if (nextEmpty && !this.refs.empty.hidden) {
          this.refs.empty.hidden = nextEmpty.hasAttribute('hidden');
        }
        this.refs.grid.hidden = this.#productCount === 0;
        this.#syncMerch();
      } else if (nextGrid) {
        const fragment = document.createDocumentFragment();
        for (const item of [...nextGrid.querySelectorAll(':scope > [data-bt-plp-product]')]) {
          this.#productCount += 1;
          fragment.append(item);
          appendMerchAfter(
            fragment,
            this.#merch,
            this.#productCount,
            this.#isDesktop(),
            this,
            this.#hasActiveFilters
          );
        }
        this.refs.grid.append(fragment);
        this.#placeMerch();
        this.#syncShopTheLook();
      }

      this.#filterSort?.setTotal(this.#total);
      this.#syncPagination();
    } catch (error) {
      console.warn('[bt-collection-shopify] Section render failed', error);
      if (options.replace) this.#showEmpty();
    } finally {
      this.#loading = false;
      this.#hideLoading();
      this.#hideMoreLoading();
    }
  }

  #syncMerch() {
    this.#stripMerch();
    if (this.#hasActiveFilters) {
      this.#placeMerch();
      this.#syncShopTheLook();
      return;
    }
    appendPlacedMerch(this.refs.grid, this.#merch, this, false);
    this.#insertInlineMerch();
    this.#placeMerch();
    this.#syncShopTheLook();
  }

  #stripMerch() {
    this.refs.grid.querySelectorAll(':scope > [data-bt-merch-type]').forEach((node) => node.remove());
  }

  #insertInlineMerch() {
    if (this.#hasActiveFilters) return;
    const products = [...this.refs.grid.querySelectorAll(':scope > [data-bt-plp-product]')];
    let count = 0;
    for (const product of products) {
      count += 1;
      const fragment = document.createDocumentFragment();
      appendMerchAfter(fragment, this.#merch, count, this.#isDesktop(), this, false);
      if (fragment.childNodes.length) product.after(fragment);
    }
  }

  #placeMerch() {
    placeMerchTiles({
      grid: this.refs.grid,
      merch: this.#merch,
      productCount: this.#productCount,
      hasMore: this.#hasMore(),
      isDesktop: this.#isDesktop(),
    });
  }

  #syncShopTheLook() {
    syncShopTheLookGutter(this.refs.grid, this.#isDesktop());
  }

  #onLayoutChange = () => {
    this.#syncMerch();
  };

  #onResize = () => {
    if (this.#resizeTimer) clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#syncShopTheLook(), 150);
  };

  /**
   * @param {MouseEvent} event
   */
  #onShopTheLookClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openBtn = target.closest('[data-shop-the-look-open]');
    if (!(openBtn instanceof HTMLElement)) return;

    const lookId = openBtn.dataset.lookId;
    if (!lookId) return;

    event.preventDefault();
    this.#openShopTheLook(lookId, openBtn);
  };

  /**
   * @param {string} lookId
   * @param {HTMLElement} trigger
   */
  #openShopTheLook(lookId, trigger) {
    const drawer = this.querySelector('shop-the-look-drawer');
    if (drawer && typeof drawer.openLook === 'function') {
      drawer.openLook(lookId, trigger);
      return;
    }

    customElements.whenDefined('shop-the-look-drawer').then(() => {
      const next = this.querySelector('shop-the-look-drawer');
      if (next && typeof next.openLook === 'function') {
        next.openLook(lookId, trigger);
      }
    });
  }

  #showEmpty() {
    this.#hideLoading();
    this.refs.grid.hidden = true;
    this.refs.grid.replaceChildren();
    this.refs.sentinel.hidden = true;
    this.#setLoadMoreVisible(false);
    this.refs.showing.hidden = true;
    this.#hideMoreLoading();
    this.refs.empty.hidden = false;
  }

  #showLoading() {
    this.refs.empty.hidden = true;
    if (this.refs.loading) this.refs.loading.hidden = false;
    this.refs.grid.hidden = true;
    this.refs.sentinel.hidden = true;
    this.#setLoadMoreVisible(false);
    this.refs.showing.hidden = true;
    this.#hideMoreLoading();
    this.setAttribute('aria-busy', 'true');
  }

  #hideLoading() {
    if (this.refs.loading) this.refs.loading.hidden = true;
    if (this.refs.more.hidden) this.removeAttribute('aria-busy');
  }

  /**
   * @param {boolean} visible
   */
  #setLoadMoreVisible(visible) {
    this.refs.loadMore.hidden = !visible;
    this.refs.loadMore.disabled = !visible;
  }

  #updateShowing() {
    const template = this.dataset.showingTemplate || 'Showing __SHOWN__ of __TOTAL__';
    this.refs.showing.textContent = template
      .replaceAll('__SHOWN__', String(this.#productCount))
      .replaceAll('__TOTAL__', String(this.#total));
  }

  #syncShowing() {
    const visible = this.#productCount > 0 && !this.refs.grid.hidden;
    this.refs.showing.hidden = !visible;
    if (visible) this.#updateShowing();
  }

  /**
   * @param {boolean} hasMore
   */
  #syncSentinel(hasMore) {
    const showChrome = hasMore || !this.refs.showing.hidden || !this.refs.more.hidden;
    this.refs.sentinel.hidden = !showChrome;
    this.refs.sentinel.toggleAttribute('data-exhausted', !hasMore);
  }

  #showMoreLoading() {
    this.refs.more.hidden = false;
    this.#setLoadMoreVisible(false);
    this.setAttribute('aria-busy', 'true');
  }

  #hideMoreLoading() {
    this.refs.more.hidden = true;
    const showButton = this.#hasMore() && !this.#usesInfinite() && !this.refs.grid.hidden;
    this.#setLoadMoreVisible(showButton);
    this.#syncShowing();
    if (!this.refs.loading || this.refs.loading.hidden) this.removeAttribute('aria-busy');
  }

  #observeSentinel() {
    this.#observer?.disconnect();
    this.#observer = null;
    if (!this.#usesInfinite()) return;

    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      if (!this.#hasMore()) return;
      this.#loadPage(this.#page + 1, { append: true });
    });
    this.#observer.observe(this.refs.sentinel);
  }

  #syncPagination() {
    const hasMore = this.#hasMore();
    const infinite = this.#usesInfinite();

    if (this.#productCount === 0 && this.#total === 0) {
      this.refs.grid.hidden = true;
      this.refs.empty.hidden = false;
      this.refs.sentinel.hidden = true;
      this.#setLoadMoreVisible(false);
      this.refs.showing.hidden = true;
      return;
    }

    this.refs.empty.hidden = true;
    this.refs.grid.hidden = false;
    this.#syncShowing();

    if (infinite) {
      this.#setLoadMoreVisible(false);
      this.#syncSentinel(hasMore);
      if (!hasMore || !this.#observer) return;
      this.#observer.unobserve(this.refs.sentinel);
      this.#observer.observe(this.refs.sentinel);
      return;
    }

    this.#setLoadMoreVisible(hasMore && !this.#loading);
    this.#syncSentinel(hasMore);
  }
}

if (!customElements.get('bt-collection-shopify')) {
  customElements.define('bt-collection-shopify', BtCollectionShopify);
}
